// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { ProductActionsStyle } from "@sc/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@sc/App/Components/Product/ProductGridTemplate";
import ProductPriceAndActions, { ProductPriceAndActionsStyle } from "@sc/App/Components/Product/ProductPriceAndActions";
import { mergeWithCss } from "@sc/XDL/Utilities";
import React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    product: ProductModel;
    widgetId: string;
    showImage?: boolean;
    showTitle?: boolean;
    showBrand?: boolean;
    showPartNumbers?: boolean;
    showPrice?: boolean;
    showAvailability?: boolean;
    showAddToCart?: boolean;
    showAddToList?: boolean;
    showTextBadges?: boolean;
    productMiniView?: boolean;
    maximumNumberOfTextBadges?: number;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    extendedStyles?: ProductMiniCardStyles;
    itemListName?: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
};

export interface ProductMiniCardStyles {
    productGridContainerStyles?: ProductGridContainerStyle;
    descritopnContainerStyles?: InjectableCss;
    imageAndDescriptionSection?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceStyles?: ProductPriceStyles;
    availabilityStyles?: ProductAvailabilityStyles;
    productActions?: ProductActionsStyle;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    autoDeliveryWrapper?: InjectableCss;
    availabilityyWrapper?: InjectableCss;
    badgesWrapper?: InjectableCss;
    productAvailabilityStyle?: ProductAvailabilityStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productPriceAndActionsTopSectionStyles?: ProductPriceAndActionsStyle;
    brandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
}

// Xng-Changes: please check ProductMiniCardExtensions.ts for styles
export const productMiniCardStyles: ProductMiniCardStyles = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const ProductMiniCard = ({
    product,
    productInfo,
    widgetId,
    showImage,
    showTitle,
    showBrand,
    showPartNumbers,
    showPrice,
    showAvailability,
    showAddToCart,
    showAddToList,
    showTextBadges,
    maximumNumberOfTextBadges,
    showImageBadges,
    maximumNumberOfImageBadges,
    extendedStyles,
    updateProductInfo,
    productMiniView,
    itemListName,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productMiniCardStyles, extendedStyles));
    if (!productInfo || !product) {
        return null;
    }

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onUnitOfMeasureChanged: unitOfMeasure => {
            updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
        },
        onQtyOrderedChanged: qtyOrdered => {
            updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
        },
    };
    // XNG-CHANGE
    const productDetailPath = productContext.productInfo.productDetailPath;

    return (
        <ProductContext.Provider value={productContext}>
            <ProductGridContainer
                extendedStyles={styles.productGridContainerStyles}
                className={`${productMiniView && "product-mini-view"}`}
            >
                <ProductGridContainer.Info>
                    <StyledWrapper {...styles.imageAndDescriptionSection}>
                        {showImage && (
                            <ProductGridContainer.Image>
                                <ProductImage
                                    product={productContext}
                                    showSmallImage={true}
                                    extendedStyles={styles.productImage}
                                />
                            </ProductGridContainer.Image>
                        )}
                        <StyledWrapper className="product-description" {...styles.descritopnContainerStyles}>
                            <StyledWrapper {...styles.brandWrapper}>
                                {showBrand && product.brand && (
                                    <ProductBrand
                                        brand={product.brand}
                                        extendedStyles={styles.productBrandStyles}
                                        {...{ productDetailPagePath: productDetailPath }}
                                    />
                                )}
                            </StyledWrapper>
                            {showTitle && (
                                <ProductDescription
                                    product={productContext}
                                    extendedStyles={styles.productDescription}
                                />
                            )}
                            {showPartNumbers && (
                                <ProductPartNumbers
                                    productNumber={product.productNumber}
                                    customerProductNumber={product.customerProductNumber}
                                    manufacturerItem={product.manufacturerItem}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                            )}
                            <ProductPriceAndActions
                                showAvailability={showAvailability}
                                showAddToList={false}
                                showPrice={showPrice}
                                showTextBadges={showTextBadges}
                                maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                                showImageBadges={showImageBadges}
                                maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                                showAddToCartActions={false}
                                showOriginPrice={false}
                                containerClassName="pricing-info-container"
                                extendedStyles={styles.productPriceAndActionsTopSectionStyles}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </ProductGridContainer.Info>
                <ProductGridContainer.PriceAndActions>
                    <ProductPriceAndActions
                        showAvailability={false}
                        showAddToList={showAddToList}
                        showPrice={false}
                        showTextBadges={false}
                        maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                        showAddToCartActions={showAddToCart}
                        extendedStyles={styles.productPriceAndActionsStyles}
                        containerClassName="pricing-info-container"
                        itemListName={itemListName}
                    />
                </ProductGridContainer.PriceAndActions>
            </ProductGridContainer>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductMiniCard);
